import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AssessQueryKeys, ScoresQueryKeys, useScoresApi } from '@/api';
import { CreateUpdateScoreRequest, GetBsimmAssessmentViewRequest } from '@/api/api-queries/duck';

export const useGetBsimmAssessmentView = (request: GetBsimmAssessmentViewRequest) => {
  const { scoresApi } = useScoresApi();

  const { data, isLoading } = useQuery({
    queryKey: [ScoresQueryKeys.bsimmAssessmentView, request],
    queryFn: async () => {
      const response = await scoresApi.bsimmAssessmentViewApiScoresBsimmAssessmentAssessmentIdGet(
        request?.assessmentId,
      );

      return response.data;
    },
    retry: 3,
  });

  return {
    bsimmAssessmentView: data || '',
    isBsimmLoading: isLoading,
  };
};

export const useGetScoresByAssessmentId = (assessmentId: string) => {
  const { scoresApi } = useScoresApi();

  const { data, isLoading, isError } = useQuery({
    queryKey: [ScoresQueryKeys.getScoresByAssessmentId, assessmentId],
    queryFn: async () => {
      const response = await scoresApi.getScoresRootApiScoresGet(
        assessmentId,
      );

      return response.data;
    },
    retry: 3,
  });

  return {
    scores: data?.activity_scores || '',
    isScoresLoading: isLoading,
    isScoresError: isError,
  };
};

export const useUpdateScoresForm = () => {
  const { scoresApi } = useScoresApi();

  const { data, isLoading } = useQuery({
    queryKey: [ScoresQueryKeys.updateScoreForm],
    queryFn: async () => {
      const response = await scoresApi.updateScoreFormApiScoresUpdateFormGet();

      return response.data;
    },
    retry: 3,
  });

  return {
    updateScoreForm: data || '',
    isUpdateScoreFormLoading: isLoading,
  };
};

export const useGetRadarGraphView = (assessmentId: string) => {
  const { scoresApi } = useScoresApi();

  const { data, isLoading } = useQuery({
    queryKey: [ScoresQueryKeys.radarGraphView, assessmentId],
    queryFn: async () => {
      const response = await scoresApi.radarGraphViewApiScoresRadarGraphAssessmentIdGet(
        assessmentId,
      );

      return response.data;
    },
    retry: 3,
  });

  return {
    radarGraphView: data || '',
    isRadarGraphLoading: isLoading,
  };
};

export const useCreateUpdateScores = () => {
  const queryClient = useQueryClient();
  const { scoresApi } = useScoresApi();

  const { mutateAsync, isPending, isError } = useMutation({
    mutationFn: async (request: CreateUpdateScoreRequest) => {
      const response = await scoresApi.createScoreWithPathParamApiScoresAssessmentIdPost(
        request.assessmentId,
        request.scoreUpdateRequest,
      );

      return response.data;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [AssessQueryKeys.assessmentsList],
      });

      await queryClient.invalidateQueries({
        queryKey: [ScoresQueryKeys.getScoresByAssessmentId, variables.assessmentId],
      });
    },
  });

  return {
    createUpdateScore: mutateAsync,
    isActionLoading: isPending,
    isActionError: isError,
  };
};
